/**
 * Report React Query Hooks
 *
 * TanStack Query hooks for report operations
 */

import { usePermiso } from '@/application/auth'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  ReportFilters,
  CreateReportData,
  UpdateReportData,
  AmendReportData,
} from '@/domain/report'
import type { ReportStatus } from '@/config/constants'
import { useAuth } from '@/application/auth'
import * as reportService from './report.service'

// =============================================================================
// Query Keys
// =============================================================================

export const reportKeys = {
  all: ['reports'] as const,
  lists: () => [...reportKeys.all, 'list'] as const,
  list: (filters?: ReportFilters) => [...reportKeys.lists(), filters] as const,
  details: () => [...reportKeys.all, 'detail'] as const,
  detail: (id: string) => [...reportKeys.details(), id] as const,
  studyReports: (studyId: string) => [...reportKeys.all, 'study', studyId] as const,
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Get paginated/filtered list of reports
 */
export function useReports(options?: ReportFilters & { enabled?: boolean }) {
  const { isAuthenticated } = useAuth()
  const { enabled = true, ...filters } = options || {}
  return useQuery({
    queryKey: reportKeys.list(Object.keys(filters).length > 0 ? filters : undefined),
    queryFn: () => reportService.getReports(Object.keys(filters).length > 0 ? filters : undefined),
    enabled: isAuthenticated && enabled,
    staleTime: 1000 * 60 * 2, // 2 minutes
  })
}

/**
 * Get a single report by ID
 */
export function useReport(id: string | undefined) {
  return useQuery({
    queryKey: reportKeys.detail(id!),
    queryFn: () => reportService.getReportById(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
  })
}

/**
 * Get reports for a specific study
 */
export function useStudyReports(studyId: string | undefined) {
  // report:view:all, que es lo que exige la ruta. El tecnologo abre el visor
  // del estudio que acaba de subir y esta consulta le devolvia 403.
  const puede = usePermiso('report:view:all')
  return useQuery({
    queryKey: reportKeys.studyReports(studyId!),
    queryFn: () => reportService.getStudyReports(studyId!),
    enabled: !!studyId && puede,
    staleTime: 1000 * 60 * 2,
  })
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Create a new report
 */
export function useCreateReport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateReportData) => reportService.createReport(data),
    onSuccess: (newReport) => {
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() })
      // Invalidate study reports
      queryClient.invalidateQueries({
        queryKey: reportKeys.studyReports(newReport.studyId),
      })
      // Add to cache
      queryClient.setQueryData(reportKeys.detail(newReport.id), newReport)
    },
  })
}

/**
 * Update an existing report
 */
export function useUpdateReport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReportData }) =>
      reportService.updateReport(id, data),
    onSuccess: (updatedReport) => {
      // Update cache
      queryClient.setQueryData(reportKeys.detail(updatedReport.id), updatedReport)
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() })
      // Invalidate study reports
      queryClient.invalidateQueries({
        queryKey: reportKeys.studyReports(updatedReport.studyId),
      })
    },
  })
}

/**
 * Update report status
 */
export function useUpdateReportStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ReportStatus }) =>
      reportService.updateReportStatus(id, status),
    onSuccess: (updatedReport) => {
      queryClient.setQueryData(reportKeys.detail(updatedReport.id), updatedReport)
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: reportKeys.studyReports(updatedReport.studyId),
      })
    },
  })
}

/**
 * Sign a report
 * Also invalidates worklist queries since signing auto-completes the worklist item
 */
export function useSignReport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => reportService.signReport(id),
    onSuccess: (signedReport) => {
      queryClient.setQueryData(reportKeys.detail(signedReport.id), signedReport)
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: reportKeys.studyReports(signedReport.studyId),
      })
      // Invalidate worklist queries since signing auto-completes the worklist item
      queryClient.invalidateQueries({ queryKey: ['worklist'] })
    },
  })
}

/**
 * Amend a report
 */
export function useAmendReport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AmendReportData }) =>
      reportService.amendReport(id, data),
    onSuccess: (amendedReport) => {
      // Add new amendment to cache
      queryClient.setQueryData(reportKeys.detail(amendedReport.id), amendedReport)
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: reportKeys.studyReports(amendedReport.studyId),
      })
    },
  })
}

/**
 * Delete a report
 */
export function useDeleteReport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => reportService.deleteReport(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: reportKeys.detail(deletedId) })
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() })
    },
  })
}
